import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addCompany,
  deleteCompany,
  getCompanies,
  getCompaniesWithCardCount,
  getCompanyById,
  updateCompany,
  type Company,
  type CompanyInput,
} from "@/api/companies";
import { qk } from "@/api/queryKeys";
import { toast } from "@/lib/toast";

export function useCompanies() {
  return useQuery({
    queryKey: qk.companies.list(),
    queryFn: getCompanies,
  });
}

export function useCompaniesWithCardCount() {
  return useQuery({
    queryKey: qk.companies.listWithCardCounts(),
    queryFn: getCompaniesWithCardCount,
  });
}

export function useCompany(id: number | null) {
  return useQuery({
    queryKey: qk.companies.detail(id ?? -1),
    queryFn: () => getCompanyById(id as number),
    enabled: id !== null,
  });
}

export function useAddCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CompanyInput) => addCompany(input),
    onSuccess: (company: Company) => {
      void queryClient.invalidateQueries({ queryKey: qk.companies.list() });
      queryClient.setQueryData(qk.companies.detail(company.id), company);
    },
  });
}

export function useUpdateCompany(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CompanyInput) => updateCompany(id, input),
    onSuccess: (company: Company) => {
      void queryClient.invalidateQueries({ queryKey: qk.companies.list() });
      queryClient.setQueryData(qk.companies.detail(id), company);
    },
  });
}

export function useDeleteCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { id: number; name: string }) => deleteCompany(params.id),
    meta: { errorTitle: "Couldn't delete subcontractor" },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: qk.companies.all() });
      void queryClient.invalidateQueries({ queryKey: qk.cards.all() });
      toast.success(`${variables.name} deleted`);
    },
  });
}
